/**
 * Background-poll endpoints whose failures must NOT show the user-facing error toast.
 *
 * By default every API error that reaches handleError (in api.tsx) shows the generic red error
 * toast. That's correct for user-initiated actions. But a few endpoints are non-critical background
 * polls that run automatically (often on a timer, on every page): a transient 5xx / network blip on
 * one of these has no user value — surfacing it just produces a scary toast for something the user
 * never initiated (e.g. RINGIL-APP-SX, where a 503 on the notification poll showed a 30s error
 * toast). Listed here, their failures are still logged and reported to Sentry (so the failure stays
 * visible for monitoring) — only the toast is suppressed.
 *
 * Only safe-to-ignore reads (GET polls) belong here. A per-call `callbacks.onError` still wins; this
 * is the central default so individual poll call sites don't each have to pass a suppressing onError.
 */
export const SILENT_API_ERROR_DENYLIST: ReadonlySet<string> = new Set([
  // Web-app alert poll (AppMessagesProvider, every 60s app-wide)
  'getWebAppAlert',

  // Notification polls (unread count + list, run on mount/poll across the app)
  'getCompanyNotificationsForInheritor',
  'cuCountUnreadNotification',
  'cuListNotifications',

  // Async jobs poll (jobs-monitoring dev page, every 5s)
  'listMyAsyncJobs',
  'adminListAsyncJobs',
  'getDuplicationJobDetail',
  'getPushToken',
  'getPushSubscriptionToken',
  'getMultiduplicationV3JobDetail',
  // reservation multi-duplication job detail poll fallback
  'getReservationMultiDuplicationJobDetail',
  // jobs-center list + export job detail poll fallbacks (15s while push is down)
  'listCompanyJobs',
  'getAsyncJobDetail',
  // menu badge counts (push-triggered refresh + 60s poll fallback, app-wide)
  'getJobBadgeCounts',
]);
